import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordField } from "@/components/drivers/PasswordField";
import { generatePassword } from "@/lib/password";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Something went wrong";
};

// carrier: null = closed; {} = new carrier; {id, name} = add admin to that carrier
export const CarrierDialog = ({ carrier, api, onClose, onDone }) => {
  const [f, setF] = useState({ company: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (carrier) { setF({ company: "", name: "", email: "", password: generatePassword() }); setError(""); } }, [carrier]);
  const isNew = carrier && !carrier.id;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const valid = (!isNew || f.company.trim()) && f.name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.password.length >= 8;
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = isNew
        ? await api.post("/owner/carriers", f)
        : await api.post(`/owner/carriers/${carrier.id}/admins`, { name: f.name, email: f.email, password: f.password });
      const sent = (isNew ? data.admin : data).email_sent;
      toast.success(isNew ? `${f.company} created` : "Admin added");
      onDone({ title: isNew ? `${f.company} is ready` : `Admin added to ${carrier.name}`, email: f.email.trim().toLowerCase(), password: f.password, email_sent: sent });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const input = (k, label, ph, type = "text") => (
    <div><Label htmlFor={`carrier-${k}`}>{label}</Label><Input id={`carrier-${k}`} type={type} value={f[k]} onChange={set(k)} placeholder={ph} className="mt-1" data-testid={`carrier-${k}-input`} /></div>
  );
  return (
    <Dialog open={!!carrier} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="carrier-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New carrier" : `Add admin to ${carrier?.name}`}</DialogTitle>
          <DialogDescription>{isNew ? "Creates an empty, separate workspace and its first admin login." : "This person gets admin access to this carrier's workspace only."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isNew && input("company", "Carrier company name", "e.g. Blue Line Freight LLC")}
          {input("name", "Admin full name", "e.g. Maria Lopez")}
          {input("email", "Admin email", "maria@bluelinefreight.com", "email")}
          <PasswordField value={f.password} onChange={set("password")} />
          {error && <p className="text-sm text-rose-600" data-testid="carrier-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} className="bg-slate-900 hover:bg-slate-800" data-testid="carrier-save-btn">{busy ? "Creating…" : isNew ? "Create carrier" : "Add admin"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
